import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  Button,
  ButtonColor,
  ButtonSize,
  ButtonVariant,
  CameraIcon,
  CheckIcon,
  ComboBox,
  EditIcon,
  GalleryIcon,
  SelectOption,
  Spinner,
  Text,
  TextField,
  TextVariant,
} from "../../ui";
import { FullScreenDialog } from "../../FullScreenDialog";
import {
  CreatePartOrderItemWithoutPartOrderInput,
  Mutation,
  MutationGenerateGenericPresignedUrlArgs,
  PartsStore,
  Query,
} from "../../../generated/nest-graphql";
import { GET_PARTS_STORES } from "../../../graphql/queries/getPartsStores";
import { GENERATE_GENERIC_PRESIGNED_URL } from "../../../graphql/mutations/generateGenericPresignedUrl";
import { CREATE_FILE } from "../../../graphql/mutations/createFile";
import { useSimpleS3Upload } from "../../../hooks/useS3Upload";
import { useAiAxiosClient } from "../../../lib/aiAxiosClient";
import { ExtractedLine, extractPartsFromReceipt, RECEIPT_SCAN_ENABLED, toItemsInput } from "../../../lib/receiptScan";
import { ReceiptLines } from "../ReceiptLines";

/** RECEIPT-SCAN: `idle` is the manual form, which is where the flow ends either way. */
type ScanState = "idle" | "reading" | "confirm";

export type PartOrderJobOption = {
  jobId: string;
  jobNumber?: string | null;
  contactId?: string | null;
  label: string;
};

/**
 * Logs an order the technician placed themselves. No approval step — ops reconciles
 * later. A part order has nowhere to hold a file, so the receipt is stored as a
 * contact file tagged `part-order-receipt`, with the order id in the caption.
 */
export const AddPartOrderDialog: React.FC<{
  open: boolean;
  jobs: PartOrderJobOption[];
  onClose: () => void;
  onCreate: (input: {
    job: string;
    orderNumber: string | null;
    store: string | null;
    notes: string | null;
    items: CreatePartOrderItemWithoutPartOrderInput[];
  }) => Promise<string | null>;
  submitting: boolean;
}> = ({ open, jobs, onClose, onCreate, submitting }) => {
  const [jobId, setJobId] = useState("");
  const [store, setStore] = useState<PartsStore | null>(null);
  const [orderNumber, setOrderNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [receipt, setReceipt] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [lines, setLines] = useState<ExtractedLine[]>([]);
  const [scanState, setScanState] = useState<ScanState>("idle");
  const [scanError, setScanError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setJobId(jobs.length === 1 ? jobs[0].jobId : "");
      setStore(null);
      setOrderNumber("");
      setNotes("");
      setReceipt(null);
      setUploadError(null);
      setLines([]);
      setScanState("idle");
      setScanError(null);
    }
  }, [open, jobs]);

  const { data: storesData } = useQuery<Query>(GET_PARTS_STORES, {
    variables: { filter: { deactivated: { $ne: true } } },
    skip: !open,
  });

  const stores = useMemo(() => (storesData?.getPartsStores ?? []).filter(Boolean) as PartsStore[], [storesData]);
  const selectedJob = jobs.find((job) => job.jobId === jobId);
  const contactId = selectedJob?.contactId ?? "";

  const [generatePresignedUrl] = useMutation<Mutation, MutationGenerateGenericPresignedUrlArgs>(
    GENERATE_GENERIC_PRESIGNED_URL
  );
  const [createFile] = useMutation(CREATE_FILE);

  const createdOrderIdRef = React.useRef<string | null>(null);

  const aiClient = useAiAxiosClient();

  /** Guards against a slow first scan landing on top of a second photo's result. */
  const scanTokenRef = React.useRef(0);

  /**
   * RECEIPT-SCAN. Prefills only fields the technician has left empty — a scan must
   * never overwrite something they typed — and every failure lands back on the manual
   * form with the photo still attached.
   */
  const scanReceipt = async (file: File) => {
    const token = (scanTokenRef.current += 1);
    setScanError(null);
    // The attached photo just changed, so parts read off the previous one are no
    // longer this order's — drop them even if this scan goes on to fail.
    setLines([]);
    setScanState("reading");

    try {
      const extracted = await extractPartsFromReceipt(aiClient, file);
      if (scanTokenRef.current !== token) return;

      if (extracted.orderNumber && !orderNumber.trim()) setOrderNumber(extracted.orderNumber);
      if (extracted.notes && !notes.trim()) setNotes(extracted.notes);
      if (extracted.storeName && !store) {
        // Only a unique hit prefills. "Advance" matching three Advance branches must
        // leave the field blank — a silently wrong store is worse than an empty one,
        // since ops reconciles against it.
        const wanted = extracted.storeName.toLowerCase();
        const matches = stores.filter((option) =>
          `${option.vendor ?? ""} ${option.name}`.toLowerCase().includes(wanted)
        );
        if (matches.length === 1) setStore(matches[0]);
      }

      if (!extracted.lines.length) {
        setScanState("idle");
        setScanError("We could not read any parts off that photo. Add them in ops, or type the order below.");
        return;
      }

      setLines(extracted.lines);
      setScanState("confirm");
    } catch (error) {
      if (scanTokenRef.current !== token) return;
      setScanState("idle");
      setScanError("The receipt could not be read. The photo is still attached — type the order below.");
    }
  };

  const pickReceipt = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setReceipt(file);
    // Clearing the input lets the same file be re-picked — otherwise selecting it a
    // second time fires no change event, and the two inputs can't hand off to
    // each other either.
    event.target.value = "";
    // Both inputs land here, so camera and photo library both scan.
    if (file && RECEIPT_SCAN_ENABLED) void scanReceipt(file);
  };

  const { onUpload } = useSimpleS3Upload({
    contactId,
    myFiles: null,
    setMyFiles: null,
    onError: (error: any) => {
      setUploadError(error?.message ?? "The receipt could not be uploaded.");
      setUploading(false);
    },
    onUploadStart: async (fileName: string) => {
      const result = await generatePresignedUrl({
        variables: { path: `contacts/${contactId}/${fileName}` },
      });
      return result.data.generateGenericPresignedUrl;
    },
    onUploadDone: async ({ url, fileName }) => {
      await createFile({
        variables: {
          createFileInput: {
            contact: contactId,
            path: `contacts/${contactId}/${fileName}`,
            url,
            share: false,
            fileName,
            jobId,
            category: "part-order-receipt",
            // The only link back to the order this receipt belongs to.
            caption: createdOrderIdRef.current ? `part-order:${createdOrderIdRef.current}` : "part-order",
          },
        },
      });
    },
  });

  const submit = async () => {
    setUploadError(null);
    const newOrderId = await onCreate({
      job: jobId,
      orderNumber: orderNumber.trim() || null,
      store: store?.id ?? null,
      notes: notes.trim() || null,
      items: toItemsInput(lines),
    });

    // The order is the record that matters; a failed photo should not lose it, so
    // the upload runs after creation and reports separately.
    if (newOrderId && receipt) {
      createdOrderIdRef.current = newOrderId;
      setUploading(true);
      await onUpload([receipt]);
      setUploading(false);
    }
    onClose();
  };

  const busy = submitting || uploading;

  const abandonScan = () => {
    // Bumping the token drops an in-flight scan, so a late result cannot reopen this.
    scanTokenRef.current += 1;
    setLines([]);
    setScanError(null);
    setScanState("idle");
  };

  if (scanState !== "idle") {
    return (
      <FullScreenDialog title="Add part order" open={open} onClose={undefined}>
        <div className="p-4 flex flex-col gap-4">
          {scanState === "reading" ? (
            <div className="flex flex-col items-center gap-3 py-10">
              <Spinner />
              <Text variant={TextVariant.BODY1}>Reading the receipt…</Text>
              <Text variant={TextVariant.BODY2} className="text-gray-400">
                This takes a few seconds.
              </Text>
            </div>
          ) : (
            <>
              <Text variant={TextVariant.BODY1}>
                {lines.length} {lines.length === 1 ? "part" : "parts"} found on the receipt.
              </Text>
              <Text variant={TextVariant.BODY2} className="text-gray-400">
                Check them before saving. Ops can correct anything that came out wrong.
              </Text>
              <ReceiptLines lines={lines} />
              <Button
                variant={ButtonVariant.CONTAINED}
                color={ButtonColor.PRIMARY}
                size={ButtonSize.LARGE}
                startIcon={<CheckIcon />}
                onClick={() => setScanState("idle")}
              >
                Looks right
              </Button>
            </>
          )}
          <Button
            variant={ButtonVariant.OUTLINED}
            size={ButtonSize.LARGE}
            startIcon={<EditIcon />}
            onClick={abandonScan}
          >
            Enter manually
          </Button>
        </div>
      </FullScreenDialog>
    );
  }

  return (
    <FullScreenDialog title="Add part order" open={open} onClose={busy ? undefined : onClose}>
      <div className="p-4 flex flex-col gap-4">
        <TextField
          select
          label="Job"
          value={jobId}
          onChange={setJobId}
          fullWidth
          required
          disabled={busy}
          helperText="Only today's jobs are listed."
        >
          {jobs.map((job) => (
            <SelectOption key={job.jobId} value={job.jobId}>
              {job.label}
            </SelectOption>
          ))}
        </TextField>

        <ComboBox<PartsStore>
          value={store}
          onChange={setStore}
          options={stores}
          getOptionLabel={(option) => `${option.vendor ?? ""} — ${option.name}`.trim()}
          disabled={busy}
          label="Store (optional)"
          fullWidth
        />

        <TextField
          label="Order number (optional)"
          value={orderNumber}
          onChange={setOrderNumber}
          fullWidth
          disabled={busy}
        />

        <TextField
          label="Notes (optional)"
          value={notes}
          onChange={setNotes}
          fullWidth
          multiline
          rows={3}
          disabled={busy}
        />

        <div className="flex flex-col gap-2">
          <Text variant={TextVariant.BODY2} className="text-gray-400">
            Photo of the invoice or receipt
          </Text>
          <div className="flex gap-2">
            <Button
              variant={ButtonVariant.OUTLINED}
              as="label"
              fullWidth
              startIcon={<CameraIcon />}
              disabled={busy || !contactId}
            >
              Take photo
              {/* `capture` sends the OS straight to the rear camera. */}
              <input hidden type="file" accept="image/*" capture="environment" onChange={pickReceipt} />
            </Button>
            <Button
              variant={ButtonVariant.OUTLINED}
              as="label"
              fullWidth
              startIcon={<GalleryIcon />}
              disabled={busy || !contactId}
            >
              Upload
              {/* No `capture` here, so the OS offers the gallery and file browser. */}
              <input hidden type="file" accept="image/*" onChange={pickReceipt} />
            </Button>
          </div>
          {receipt && (
            <Text variant={TextVariant.BODY2} className="text-gray-300">
              Attached: {receipt.name}
            </Text>
          )}
          {scanError && (
            <Text variant={TextVariant.BODY2} style={{ color: "#f0a23e" }}>
              {scanError}
            </Text>
          )}
          {uploadError && (
            <Text variant={TextVariant.BODY2} style={{ color: "#f04e23" }}>
              {uploadError} The order was still saved.
            </Text>
          )}
        </div>

        {lines.length > 0 && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <Text variant={TextVariant.BODY2} className="text-gray-400">
                Parts from the receipt ({lines.length})
              </Text>
              <Button size={ButtonSize.SMALL} onClick={() => setLines([])} disabled={busy}>
                Remove
              </Button>
            </div>
            <ReceiptLines lines={lines} />
          </div>
        )}

        <Button
          variant={ButtonVariant.CONTAINED}
          color={ButtonColor.PRIMARY}
          size={ButtonSize.LARGE}
          disabled={busy || !jobId}
          onClick={submit}
        >
          {busy ? <Spinner size={22} /> : "Save part order"}
        </Button>
      </div>
    </FullScreenDialog>
  );
};
